import React from "react";
import { Clock, PlaneLanding, AlertCircle } from "lucide-react";
import {
  parseOfficeHours,
  CBP_TZ_TO_IANA,
  zoneOffsetHours,
  formatDelta,
} from "./parsers/officeHours";
import { parsePtl, formatPtl } from "./parsers/ptl";

export function OfficeHoursChips({ raw, t, base }) {
  const parsed = parseOfficeHours(raw);
  if (!parsed || !parsed.ok) {
    return (
      <div className="mt-1 space-y-0.5 text-[11px]">
        <div className="flex items-center gap-1.5">
          <Clock className="w-3 h-3 text-amber-500 flex-shrink-0" />
          <span className="text-[10px] uppercase tracking-wider text-slate-400">
            {t("cbp.officeHours")}
          </span>
          <span className="text-amber-400 flex items-center gap-1">
            <AlertCircle className="w-3 h-3 flex-shrink-0" />
            {t("cbp.ptlParsed.checkRequirements")}
          </span>
        </div>
        {raw && <div className="text-[10px] text-slate-400 line-clamp-1 pl-4">{raw}</div>}
      </div>
    );
  }
  const dot = <span className="text-slate-700">·</span>;
  return (
    <div className="mt-1 flex items-center gap-1.5 text-[11px]">
      <Clock className="w-3 h-3 text-slate-500 flex-shrink-0" />
      {parsed.always ? (
        <span className="font-mono font-semibold text-emerald-400">24/7</span>
      ) : (
        <>
          {parsed.dayStart !== null && (
            <span className="text-slate-400">
              {t(`cbp.dayShort.${parsed.dayStart}`)}–{t(`cbp.dayShort.${parsed.dayEnd}`)}
            </span>
          )}
          {parsed.dayStart !== null && parsed.hours && dot}
          {parsed.hours && <span className="font-mono text-slate-200">{parsed.hours}</span>}
        </>
      )}
      {parsed.tz && !parsed.always && (
        <>
          {dot}
          <TzRef tz={parsed.tz} base={base} />
        </>
      )}
    </div>
  );
}

export function TzRef({ tz, base }) {
  const baseIcao = base?.base_airport;
  const baseTzName = base?.tz_name;
  const baseStaticOffset = typeof base?.tz_offset === "number" ? base.tz_offset : null;
  const airportIana = CBP_TZ_TO_IANA[tz];

  if (!baseIcao || !airportIana || (!baseTzName && baseStaticOffset === null)) {
    return (
      <span className="font-mono text-[10px] px-1.5 py-0.5 bg-slate-800 rounded text-slate-400">
        {tz}
      </span>
    );
  }

  const airportOffset = zoneOffsetHours(airportIana);
  const baseOffset = baseTzName ? zoneOffsetHours(baseTzName) : baseStaticOffset;
  if (airportOffset === null || baseOffset === null) {
    return (
      <span className="font-mono text-[10px] px-1.5 py-0.5 bg-slate-800 rounded text-slate-400">
        {tz}
      </span>
    );
  }

  const delta = airportOffset - baseOffset;
  const deltaStr = formatDelta(delta);
  return (
    <span className="font-mono text-[10px] px-1.5 py-0.5 bg-slate-800 rounded text-slate-400">
      {baseIcao}
      {deltaStr && <span className="text-slate-300 ml-1">{deltaStr}</span>}
    </span>
  );
}

export function LandingRightsChip({ raw, t }) {
  const parsed = parsePtl(raw);
  const text = formatPtl(parsed, t);
  const uncertain = parsed === null;
  return (
    <div className="mt-1 flex items-center gap-1.5 text-[11px]">
      <PlaneLanding
        className={`w-3 h-3 flex-shrink-0 ${uncertain ? "text-amber-500" : "text-amber-400/70"}`}
      />
      <span className="text-[10px] uppercase tracking-wider text-slate-400">
        {t("cbp.landingRights")}
      </span>
      {uncertain ? (
        <span className="text-amber-400 flex items-center gap-1">
          <AlertCircle className="w-3 h-3 flex-shrink-0" />
          {t("cbp.ptlParsed.checkRequirements")}
        </span>
      ) : (
        <span className="text-slate-300">{text}</span>
      )}
    </div>
  );
}
